import { visibleColors, hiddenColor, image, onCanvas, noneHighlighted, active, inactive, pillText } from './node'
import { pixelRatio, pixelRatioWidth, pixelRatioHeight, scaleCutoff, mobile } from './utils'
import { highlighted } from './link'
import * as colors from './colors'
import * as branding from './branding'

export const reset = (context: CanvasRenderingContext2D, visible: boolean): void => {
  if (visible) {
    const fillStyle = globalThis._CONFIGURATION_.watermark === true ? context.createPattern(watermark(), 'repeat') : colors.background()
    background(context, fillStyle)
  } else {
    background(context, '#fff')
  }
}

export const link = (context: CanvasRenderingContext2D, element: object): void => {
  if (element.visible === false && (noneHighlighted() || !highlighted(element))) return
  if (!noneHighlighted() && !highlighted(element)) return
  if (!onCanvas(element.source) && !onCanvas(element.target)) return
  if (element.source.render === false || element.target.render === false) return
  context.save()
  context.beginPath()
  context.moveTo(element.source.x, element.source.y)
  context.lineTo(element.target.x, element.target.y)
  context.lineWidth = 1
  context.strokeStyle = highlighted(element) || noneHighlighted() ? colors.defaultLink() : colors.inactive()
  context.stroke()
  context.closePath()
  context.restore()
}

export const node = (context: CanvasRenderingContext2D, element: object): void => {
  if (!onCanvas(element)) return
  if (element.render === false) return
  switch (element.style) {
    case 'empty':
      emptyNode(context, element)
      break
    case 'logo':
      logoNode(context, element)
      break
    case 'label':
      labelNode(context, element)
      break
    case 'image':
      imageNode(context, element)
      break
    case 'hidden':
      hiddenNode(context, element)
      break
    default:
      console.error('Not a legal style', element.style)
  }

  if (pillText(element) !== undefined) pill(context, element.x, parseInt(element.y) + parseInt(element.pill.offset), element.radius, pillText(element), element.pill.size, pillBackground(element), pillColor(element))

  if (element.state !== undefined) stateBubble(context, element)
}

export const brandingImage = (context: CanvasRenderingContext2D): void => {
  const xO = 20
  const yO = mobile() ? 55 : 80
  const x = (globalThis._TRANSFORM_.x - xO) / -globalThis._TRANSFORM_.k
  const y = (globalThis._TRANSFORM_.y - yO) / -globalThis._TRANSFORM_.k
  const width = brandingWidth() / globalThis._TRANSFORM_.k
  const height = brandingHeight() / globalThis._TRANSFORM_.k
  context.save()
  drawImage(context, branding.image(), x, y, width, height, 1.0)
  context.restore()
}

const brandingWidth = (): number => {
  if (globalThis?._CONFIGURATION_?.branding?.width !== undefined) return globalThis._CONFIGURATION_.branding.width
  return mobile() ? 78.75 : 105
}

const brandingHeight = (): number => {
  if (globalThis?._CONFIGURATION_?.branding?.width !== undefined) return globalThis._CONFIGURATION_.branding.width / globalThis._CONFIGURATION_.branding.aspectRatio
  return mobile() ? 35.25 : 47
}

const background = (context: CanvasRenderingContext2D, color: string): void => {
  context.clearRect(0, 0, pixelRatioWidth(), pixelRatioHeight())
  context.fillStyle = color
  context.rect(0, 0, pixelRatioWidth(), pixelRatioHeight())
  context.fill()
  context.translate(globalThis._TRANSFORM_.x * pixelRatio(), globalThis._TRANSFORM_.y * pixelRatio())
  context.scale(globalThis._TRANSFORM_.k * pixelRatio(), globalThis._TRANSFORM_.k * pixelRatio())
}

const watermarkPaths = (): string[] => {
  return [
    new Path2D('M400 7.89368C398.734 8.70116 397.23 9.16907 395.617 9.16907C391.109 9.16907 387.454 5.5144 387.454 1.00615C387.454 0.665489 387.475 0.329704 387.515 0H383.413C383.386 0.331818 383.373 0.667377 383.373 1.00615C383.373 7.76853 388.855 13.2504 395.617 13.2504C397.161 13.2504 398.639 12.9645 400 12.4426V7.89368Z'),
    new Path2D('M400 389.277C398.639 388.755 397.161 388.469 395.617 388.469C389.094 388.469 383.762 393.57 383.393 400H387.485C387.846 395.826 391.349 392.55 395.617 392.55C397.23 392.55 398.734 393.018 400 393.826V389.277Z'),
    new Path2D('M378.611 383.707L378.611 400H374.529L374.53 383.707L378.611 383.707Z'),
    new Path2D('M354.472 394.941C353.06 396.353 352.273 398.155 352.113 400H348.021C348.188 397.108 349.377 394.265 351.586 392.055C356.368 387.273 364.121 387.273 368.902 392.055L366.016 394.941C362.829 391.753 357.66 391.753 354.472 394.941Z'),
    new Path2D('M39.8331 388.469C46.356 388.469 51.6877 393.57 52.057 400H47.9652C47.604 395.826 44.101 392.55 39.8331 392.55C35.5651 392.55 32.0622 395.826 31.7009 400H27.6091C27.9785 393.57 33.3101 388.469 39.8331 388.469Z'),
    new Path2D('M26.2278 392.55C21.9611 392.55 18.3255 395.719 17.5411 400H13.3037V389.149H17.3852V392.059C19.6883 389.837 22.7905 388.469 26.2278 388.469V392.55Z'),
    new Path2D('M0 389.277C4.38654 390.959 7.559 395.092 7.84086 400H3.7491C3.52437 397.403 2.08398 395.155 0 393.826V389.277Z'),
    new Path2D('M0 12.4426C1.40238 11.9048 2.68068 11.1165 3.7801 10.1324V12.5703H7.86156L7.86128 1.00606C7.86128 0.667316 7.84753 0.331788 7.82055 0H3.71844C3.75896 0.329674 3.77983 0.665429 3.77983 1.00606C3.77983 3.90123 2.2726 6.44441 0 7.89368V12.4426Z'),
    new Path2D('M13.3037 1.90677V0H17.5996C17.4658 0.61914 17.392 1.26315 17.3852 1.92557L17.3847 12.5703L13.3032 12.5703V2.02651C13.3032 1.98656 13.3034 1.94665 13.3037 1.90677Z'),
    new Path2D('M27.5887 1.00615C27.5887 0.667377 27.6024 0.331818 27.6294 0H31.7315C31.691 0.329704 31.6701 0.665489 31.6701 1.00615C31.6701 5.5144 35.3248 9.16907 39.8331 9.16907C44.3413 9.16907 47.996 5.51431 47.996 1.00606C47.996 0.665429 47.9751 0.329674 47.9346 0H52.0367C52.0637 0.331824 52.0774 0.66739 52.0774 1.00617V12.5703H47.996V10.1326C45.8297 12.0715 42.9691 13.2504 39.8331 13.2504C33.0707 13.2504 27.5887 7.76853 27.5887 1.00615Z'),
    new Path2D('M351.586 9.66417C348.939 7.0172 347.758 3.45984 348.041 0H352.143C351.846 2.41021 352.622 4.92774 354.472 6.77804C357.66 9.96585 362.829 9.96586 366.017 6.77804L368.903 9.66406C364.121 14.4458 356.368 14.4459 351.586 9.66417Z'),
    new Path2D('M374.529 12.5702L374.529 0H378.611L378.611 12.5702L374.529 12.5702Z'),
    new Path2D('M350.521 49L249 150.521L249.479 151L351 49.4786L350.521 49Z'),
    new Path2D('M49.4786 49L151 150.521L150.521 151L49 49.4786L49.4786 49Z'),
    new Path2D('M178.611 212.57L178.611 184L174.53 184L174.529 212.57L178.611 212.57Z'),
    new Path2D('M154.472 195.234C157.66 192.046 162.829 192.046 166.016 195.234L168.902 192.348C164.121 187.566 156.368 187.566 151.586 192.348C146.805 197.13 146.805 204.882 151.586 209.664C156.368 214.446 164.121 214.446 168.903 209.664L166.017 206.778C162.829 209.966 157.66 209.966 154.472 206.778C151.285 203.59 151.285 198.422 154.472 195.234Z'),
    new Path2D('M207.861 201.006C207.861 194.244 202.379 188.762 195.617 188.762C188.855 188.762 183.373 194.244 183.373 201.006C183.373 207.769 188.855 213.25 195.617 213.25C198.753 213.25 201.614 212.071 203.78 210.132V212.57H207.862L207.861 201.006ZM187.454 201.006C187.454 196.498 191.109 192.843 195.617 192.843C200.125 192.843 203.78 196.498 203.78 201.006C203.78 205.514 200.125 209.169 195.617 209.169C191.109 209.169 187.454 205.514 187.454 201.006Z'),
    new Path2D('M239.833 213.25C242.969 213.25 245.83 212.071 247.996 210.133V212.57H252.077V201.006C252.077 194.244 246.595 188.762 239.833 188.762C233.071 188.762 227.589 194.244 227.589 201.006C227.589 207.769 233.071 213.25 239.833 213.25ZM239.833 192.843C235.325 192.843 231.67 196.498 231.67 201.006C231.67 205.514 235.325 209.169 239.833 209.169C244.341 209.169 247.996 205.514 247.996 201.006C247.996 196.498 244.341 192.843 239.833 192.843Z'),
    new Path2D('M213.303 212.57H217.385L217.385 201.926C217.437 196.842 221.434 192.843 226.228 192.843V188.762C222.79 188.762 219.688 190.13 217.385 192.351V189.442H213.304V201.907C213.303 201.947 213.303 201.987 213.303 202.027V212.57Z'),
    new Path2D('M178.611 216.652V218.964C178.611 222.891 181.905 226.175 186.094 226.175C190.283 226.175 193.577 222.891 193.577 218.964V216.652H197.658V218.964C197.658 225.256 192.424 230.256 186.094 230.256C179.763 230.256 174.53 225.256 174.53 218.964V216.652H178.611Z'),
    new Path2D('M351 350.521L249.479 249L249 249.479L350.521 351L351 350.521Z'),
    new Path2D('M49 350.521L150.521 249L151 249.479L49.4786 351L49 350.521Z'),
    new Path2D('M378.611 16.6516V18.9644C378.611 22.8913 381.905 26.175 386.094 26.175C390.283 26.175 393.577 22.8913 393.577 18.9644V16.6516H397.658V18.9644C397.658 25.2562 392.424 30.2565 386.094 30.2565C379.763 30.2565 374.53 25.2562 374.53 18.9644V16.6516H378.611Z')
  ]
}

const watermark = (): HTMLElement => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  canvas.width = 400
  canvas.height = 400

  context.beginPath()
  context.fillStyle = colors.background()
  context.rect(0, 0, 400, 400)
  context.fill()
  context.closePath()

  context.beginPath()
  context.fillStyle = colors.watermark()
  for (const path of watermarkPaths()) {
    context.fill(path, 'evenodd')
  }
  context.closePath()
  context.save()

  return canvas
}

const emptyNode = (context: CanvasRenderingContext2D, element: object): void => {
  pie(context, element.x, element.y, element.radius, visibleColors(element))
}

const logoNode = (context: CanvasRenderingContext2D, element: object): void => {
  pie(context, element.x, element.y, element.radius, visibleColors(element))
  arc(context, element.x, element.y, element.radius * 0.9, colors.background())
  if (image(element) !== undefined && globalThis._TRANSFORM_.k >= scaleCutoff()) {
    context.save()
    context.beginPath()
    context.moveTo(element.x, element.y)
    context.arc(element.x, element.y, element.radius * 0.9, 0, 2 * Math.PI)
    context.closePath()
    context.clip()
    drawImage(context, image(element), element.x - (element.radius * 0.9), element.y - (element.radius * 0.9), element.radius * 1.8, element.radius * 1.8, active(element) ? 1.0 : 0.4)
    context.restore()
  }
}

const labelNode = (context: CanvasRenderingContext2D, element: object): void => {
  context.save()
  arc(context, element.x, element.y, element.radius, colors.background())
  pie(context, element.x, element.y, element.radius, visibleColors(element), strokeColor(element))
  if (image(element) !== undefined) {
    drawImage(context, image(element), parseFloat(element.x) - parseFloat(element.radius / 4), parseFloat(element.y) - (3 * parseFloat(element.radius / 4)), element.radius / 2, element.radius / 2, active(element) ? 1.0 : 0.4)
  }
  labelText(context, element)
  context.restore()
}

const imageNode = (context: CanvasRenderingContext2D, element: object): void => {
  context.save()
  arc(context, element.x, element.y, element.radius, colors.background())
  if (image(element) !== undefined) {
    drawImage(context, image(element), parseFloat(element.x) - parseFloat(element.radius), parseFloat(element.y) - parseFloat(element.radius), element.radius * 2, element.radius * 2, active(element) ? 1.0 : 0.4)
  }
  context.restore()
}

const hiddenNode = (context: CanvasRenderingContext2D, element: object): void => {
  arc(context, element.x, element.y, element.radius, hiddenColor(element))
}

const arc = (context: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string, startAngle: number = 0, endAngle: number = Math.PI * 2.0, stroke: object = {}): void => {
  context.beginPath()
  context.moveTo(x, y)
  context.arc(x, y, radius, startAngle, endAngle)
  context.closePath()
  if (stroke.color !== undefined) {
    context.lineWidth = stroke.width
    context.strokeStyle = stroke.color
    context.stroke()
  }
  context.fillStyle = color
  context.fill()
  context.closePath()
}

const pie = (context: CanvasRenderingContext2D, x: number, y: number, radius: number, colors: array, stroke: object = {}): void => {
  if (colors === undefined) return
  const angle = (2.0 / colors.length) * Math.PI
  colors.forEach(function (color, i) {
    const startAngle = i * angle
    const endAngle = startAngle + angle
    context.save()
    arc(context, x, y, radius, color, startAngle, endAngle, stroke)
    context.restore()
  })
}

const pill = (context: CanvasRenderingContext2D, x: number, y: number, radius: number, text: string, fontSize: number, background: string = '#000', foreground: string = '#fff'): void => {
  const pillRadius = fontSize * 0.85
  const horizontalOffset = text.length * pillRadius * 0.4
  context.save()
  context.beginPath()
  context.fillStyle = background
  context.moveTo(x, y)
  context.arc(x - horizontalOffset, y, pillRadius, 0, 2 * Math.PI)
  context.arc(x + horizontalOffset, y, pillRadius, 0, 2 * Math.PI)
  context.rect(x - horizontalOffset, y - pillRadius, horizontalOffset * 2, pillRadius * 2)
  context.fill()
  context.closePath()
  context.textAlign = 'center'
  context.fillStyle = foreground
  context.font = `bold ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`
  context.fillText(text, x, y + (fontSize * 0.35))
  context.restore()
}

const drawImage = (context: CanvasRenderingContext2D, image: HTMLImageElement, x: number, y: number, width: number, height: number, alpha: number): void => {
  context.globalAlpha = alpha
  if (image.complete) {
    context.save()
    try {
      context.drawImage(image, x, y, width, height)
    } catch (error) {
      console.error(error)
    }
    context.restore()
  }
}

const labelText = (context: CanvasRenderingContext2D, element: object): void => {
  const split = element.label.text
  const lineCount = split.length
  const lineOffset = lineCount === 1 ? 0 : ((lineCount * 0.5) - 0.5) * element.label.size
  const yOffset = element.y - lineOffset
  context.save()
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillStyle = inactive(element) ? colors.background() : element.label.color
  context.font = `bold ${parseInt(element.label.size)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"`
  split.forEach((name, i) => {
    context.fillText(name, element.x, yOffset + (i * element.label.size))
  })
  context.restore()
}

const pillBackground = (element: object): string => {
  return inactive(element) ? colors.inactive() : element.pill.background
}

const pillColor = (element: object): string => {
  return inactive(element) ? colors.background() : element.pill.color
}

const strokeColor = (element: object): string => {
  return inactive(element) ? colors.inactive() : element.stroke
}
